import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Copy, Check } from 'lucide-react';

interface QRShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: unknown; // Unused, kept for compatibility
  isPublic?: boolean;
  publicCode?: string;
}

/**
 * Modal component for displaying and sharing QR codes for public timelines
 */
const QRShareModal: React.FC<QRShareModalProps> = ({
  isOpen,
  onClose,
  isPublic = false,
  publicCode = ''
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Generate public timeline URL
  const baseUrl = window.location.origin;
  const publicUrl = `${baseUrl}/t/${publicCode}`;
  const qrData = publicUrl;
  const qrTitle = 'Share Timeline';
  const qrDescription = 'Scan to view live updates in real-time';

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download QR code as PNG
  const handleDownload = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = 'service-timer-qr-code.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  // If timeline is not public, show message
  if (!isPublic || !publicCode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Share Timeline</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              This timeline must be public to generate a QR code and share link.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Toggle the timeline to public in the menu to enable sharing.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">{qrTitle}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <div className="bg-white p-4 rounded-lg border-2 border-gray-200 dark:border-gray-600 mb-4">
            <QRCodeSVG
              id="qr-code-svg"
              value={qrData}
              size={256}
              level="M"
              includeMargin={true}
            />
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
            {qrDescription}
          </p>

          {/* URL copy button */}
          <div className="w-full mb-4">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <input
                type="text"
                value={publicUrl}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none"
              />
              <button
                onClick={handleCopyUrl}
                className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                title="Copy URL"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          <div className="flex gap-2 w-full">
            <button
              onClick={handleDownload}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Download
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
          <p className="text-xs text-blue-800 dark:text-blue-300">
            <strong>Public Timeline:</strong>
            <br />
            • Real-time updates
            <br />
            • Anyone with the link can view
            <br />
            • Changes sync automatically
          </p>
        </div>
      </div>
    </div>
  );
};

export default QRShareModal;
